/* eslint-disable react/require-default-props */
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import classNames from 'clsx'
import i18n from 'i18n-literally'
import PropTypes from 'prop-types'
import {
  always,
  equals,
  pipe,
  prop,
  trim,
} from 'ramda'
import { useConnect } from 'redux-bundler-hook'

import { CircularProgress } from '@mui/material'
import { makeStyles } from '@mui/styles'

import { useAllowedAnnotationTypes } from '~/src/Annotations/utils'
import { useAllowedTierItems } from '~/src/Flags/Tier'
import { NO_MORE_ITEMS_ID_LOWERCASE } from '~/src/Journal/bundle'
import Actions from '~/src/Journal/Routes/AllEventsJournal/Actions'
import {
  ALERTS_TAB,
  dateFilters,
  EVERYTHING_TAB,
  IPM_TAB,
  tabsData as initialTabsData,
  TASKS_TAB,
  useTabsData,
} from '~/src/Journal/Routes/AllEventsJournal/constants'
import EmptyJournal from '~/src/Journal/Routes/AllEventsJournal/EmptyJournal'
import Filters from '~/src/Journal/Routes/AllEventsJournal/Filters'
import JournalTable from '~/src/Journal/Routes/AllEventsJournal/Table'
import { getStepDays, getUsers } from '~/src/Journal/utils'
import { EPOCH_DATE } from '~/src/Lib/Constants'
import createLogger from '~/src/Lib/Logging'
import { datePropType, idAndNameShape, idPropType } from '~/src/Lib/PropTypes'
import {
  defer,
  EMPTY_ARRAY,
  getDateTime,
  useSetState,
} from '~/src/Lib/Utils'
import PageTitle from '~/src/UI/Shared/PageTitle'
import Tab, { Tabs } from '~/src/UI/Shared/Tab'

import { styles } from './styles'
import { getInitialDates, getQueryParam, getTaskCategory } from './utils'

// These checks are used to determine how many days to step backward on loadMore or to bracket today
// on initial fetch

// Add here any params that changing them should NOT trigger automatic date resets
const getIsFiltered = tabsData => ({
  searchTerms: Boolean,
  showUser: Boolean,
  showComplete: value => !value,
  types: value => value.length < tabsData.find(t => t.value === EVERYTHING_TAB).annotationTypes.length,
  checkedRooms: pipe(prop('length'), Boolean),
  dateFilter: value => value !== 'ALL',
  dates: always(false),
})

const shouldUpdateDates = newState => ![
  'dateFilter',
  'dates',
].some(key => key in newState)

const displayName = 'Journal/AllEvents'
const logger = createLogger(displayName)

const useStyles = makeStyles(styles, { name: displayName })

export const AllEventsJournalComponent = ({
  classes,
  deviceCount,
  facilityLocationsTerm,
  getCategory,
  initialTabValue,
  initialTaskCategory = null,
  initialShowComplete,
  me,
  relatedUsers,
  rooms,
  journal: journalProp,
  tabsData,
  doJournalFetch,
  doClearJournal,
  doUpdateQuery,
  ...props
}) => {
  const allowedAnnotationTypes = useAllowedAnnotationTypes()
  // component state
  const [state, setState] = useSetState({
    tabValue: initialTabValue,
    infiniteScrollEnabled: true,
    initialLoading: false,
    isFetching: false,
    loadMore: false,
  })
  const { tabValue, isFetching, initialLoading, loadMore } = state
  const { [tabValue]: tabData } = tabsData
  const { params: defaultParams } = tabData

  // API params-related state
  const [params, setParams] = useSetState(() => ({
    // always sets types and sometimes sets category
    ...defaultParams,
    category: initialTaskCategory || defaultParams.category,
    dateFilter: dateFilters[0].value,
    dates: getInitialDates(getStepDays(initialTabValue !== EVERYTHING_TAB, deviceCount)),
    searchTerms: '',
    showComplete: initialShowComplete ?? defaultParams.showComplete,
    showUser: false,
    checkedRooms: EMPTY_ARRAY,
  }))
  const {
    category,
    checkedRooms,
    dateFilter,
    dates,
    searchTerms,
    types,
    showComplete,
    showUser,
  } = params

  const isFiltered = getIsFiltered(tabsData)
  const items = useAllowedTierItems(journalProp)
  const journal = useMemo(() => ({ ...journalProp, items }), [items, journalProp])

  const [relatedUser, setRelatedUser] = useState()

  const inLoadingState = journal.loading || initialLoading || isFetching || loadMore

  const beforeRef = useRef(journal?.before ?? 0)

  const handleTabChange = useCallback((event, newTab) => {
    setState(({ tabValue: oldTab }) => {
      const { [newTab]: tD } = tabsData
      const { value: newTabValue, params: newParams } = tD

      if (oldTab !== newTab) {
        const reduced = newTabValue !== EVERYTHING_TAB && newTabValue !== ALERTS_TAB
        const taskCategory = getCategory(newParams)

        setParams({
          ...newParams,
          category: taskCategory,
          dates: getInitialDates(getStepDays(reduced, deviceCount), reduced),
          dateFilter: dateFilters[0].value,
        })
        defer(() => doUpdateQuery({ tab: tabsData[newTab].value }))
        return { initialLoading: true, tabValue: newTab }
      }
      return useSetState.NOOP
    })
  }, [tabsData, deviceCount, getCategory, doUpdateQuery, setParams, setState])

  const handleFilterByDate = useCallback(value => {
    const filter = dateFilters.find(f => f.value === value)
    const newDates = filter.getDates()
    setState({ infiniteScrollEnabled: !newDates })
    const reduced = value !== dateFilters[0].value
    setParams({ dateFilter: value, dates: newDates || getInitialDates(getStepDays(reduced, deviceCount), reduced) })
  }, [deviceCount, setState, setParams])

  const handleParamsChange = useCallback(newParams => {
    setState(prevState => {
      const { [prevState.tabValue]: { value: currentTabValue } } = tabsData
      setParams(old => {
        const newState = newParams?.call ? newParams(old) : newParams
        if (!shouldUpdateDates(newState)) {
          return newState
        }
        const reduced = currentTabValue !== EVERYTHING_TAB && currentTabValue !== ALERTS_TAB
        newState.dates = getInitialDates(getStepDays(reduced, deviceCount), reduced)

        return newState
      })
      return useSetState.NOOP
    })
  }, [tabsData, deviceCount, setState, setParams])

  const fetch = useCallback((...args) => {
    doJournalFetch(...args).finally(() => {
      setState({ initialLoading: false, isFetching: false, resolveLoadMore: null })
    })
    setState({ isFetching: true })
  }, [doJournalFetch, setState])

  const onLoadMore = useCallback(async () => (
    inLoadingState || isFiltered.dateFilter(dateFilter)
      ? Promise.resolve(false)
      : new Promise(resolveLoadMore => { setState({ loadMore: true, resolveLoadMore }) })
  ), [inLoadingState, isFiltered, dateFilter, setState])

  const eventsToShow = useMemo(() => {
    const filterByDate = isFiltered.dateFilter(dateFilter)
    const dateInterval = filterByDate
      ? getDateTime(dates.start).until(getDateTime(dates.end).endOf('day'))
      : null

    return journal.items.filter(item => (
      !item.uuid.startsWith(NO_MORE_ITEMS_ID_LOWERCASE)
        && (!filterByDate || dateInterval.contains(getDateTime(item.localTimestamp)))
    ))
  }, [isFiltered, journal.items, dateFilter, dates])

  const { before, loading } = journal

  const filtered = useMemo(() => Object.entries(params).reduce((f, [key, value]) => {
    const { [key]: customCheck } = isFiltered
    let paramFiltered = false
    if (customCheck) {
      paramFiltered = customCheck(value)
    } else if (Array.isArray(value)) {
      paramFiltered = Boolean(value.length)
    } else if (typeof value === 'string') {
      paramFiltered = Boolean(trim(value))
    } else {
      paramFiltered = Boolean(value)
    }
    return paramFiltered ? [...f, key] : f
  }, []), [isFiltered, params])

  const days = getStepDays(filtered.length, deviceCount)

  useEffect(() => () => doClearJournal(), [doClearJournal])

  useEffect(() => {
    beforeRef.current = journal.before
    setState(({ loadMore: old }) => (journal.before || !old ? useSetState.NOOP : { loadMore: false }))
  }, [journal.before, setState])

  // triggers on mount, on filter, search or tab change, or when loadMore
  // (at end of list or when initial load has too few items) is true
  const alreadyFetched = useRef([])
  useEffect(() => {
    if (isFetching) return
    let { start: startDate, end: endDate } = dates
    if (loadMore && journal.before) {
      endDate = startDate
      startDate = journal.before < 100 ? getDateTime(EPOCH_DATE) : endDate.minus({ days }).startOf('week')
      setParams({ dates: { end: endDate, start: startDate } })
    }
    const newParams = {
      start: startDate.toISODate(),
      end: endDate.toISODate(),
      types: types.filter(t => allowedAnnotationTypes.has(t)),
      category,
      search: searchTerms,
      room: [...checkedRooms],
      completed: showComplete ? undefined : showComplete,
      relatedUser: showUser ? relatedUser : undefined
    }

    const { [alreadyFetched.current.length - 1]: previousParams } = alreadyFetched.current
    if (previousParams && Object.entries(newParams).some(([key, value]) => {
      if (key === 'start' || key === 'end') return false
      const { [key]: previousValue } = previousParams
      return !equals(previousValue, value)
    })) {
      alreadyFetched.current = []
    }

    const newParamsAlreadyFetched = alreadyFetched.current.length
      && alreadyFetched.current.some(fetchedParams => equals(fetchedParams, newParams))
    if (newParamsAlreadyFetched) {
      return
    }

    let updateState = false
    const newState = {}
    if (loadMore) {
      updateState = true
      newState.loadMore = false
    }
    if (!journal.items.length || !equals(journal.params?.types, types)) {
      updateState = true
      newState.initialLoading = true
    }

    if (updateState) {
      setState(newState)
    }
    alreadyFetched.current.push(newParams)
    fetch(newParams, loadMore ? { replace: false } : undefined)
  }, [
    allowedAnnotationTypes,
    dates,
    params,
    isFetching,
    journal,
    loadMore,
    types,
    category,
    searchTerms,
    checkedRooms,
    showComplete,
    showUser,
    relatedUser,
    fetch,
    days,
    setParams,
    setState
  ])

  // If we don't have enough events to allow scrolling, auto-fetch further back
  useLayoutEffect(() => {
    const alreadyLoading = isFetching || loadMore || journal.loading
    const noBefore = !journal.before || !beforeRef.current
    if (isFiltered.dateFilter(dateFilter) || alreadyLoading || noBefore || eventsToShow?.length > 20) {
      return
    }
    setState(({ infiniteScrollEnabled }) => (
      infiniteScrollEnabled ? { loadMore: true } : useSetState.NOOP
    ))
  }, [isFiltered, dateFilter, eventsToShow, loading, journal.before, isFetching, loadMore, journal.loading, setState])

  useEffect(() => {
    if (inLoadingState || !before || eventsToShow.length) return
    logger.debug('[useEffect]', { before, eventsToShow, inLoadingState })
    onLoadMore()
  }, [before, eventsToShow, inLoadingState, onLoadMore])
  logger.debug('[render]', { before, eventsToShow, inLoadingState })

  const [startIndex, setStartIndex] = useState(0)

  const hasActionsClasses = tabsData[tabValue].value === IPM_TAB || tabsData[tabValue].value === TASKS_TAB ? 'aroya__journal__actions' : ''

  return initialLoading ? (
    <section className="center-all" aria-label={i18n`Journal page`}>
      <CircularProgress color="primary" size={100} />
    </section>
  ) : (
    <section className={classNames(classes.root, 'aroya__no-auto-height', hasActionsClasses)} aria-label={i18n`Journal page`}>
      <PageTitle title={i18n`Journal`} color="textPrimary" />
      <div className={classes.tabs}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          {tabsData.map(({ label, testId }) => (
            <Tab key={label} data-testid={testId} label={label} wrapped />
          ))}
        </Tabs>
      </div>
      <Actions
        classes={classes}
        openedTabValue={tabsData[tabValue].value}
      />

      <Filters
        classes={classes}
        rooms={rooms}
        tabData={tabData}
        me={me}
        {...params}
        relatedUsers={relatedUsers}
        setRelatedUser={setRelatedUser}
        handleFilterByDate={handleFilterByDate}
        onParamsChange={handleParamsChange}
        openedTabValue={tabsData[tabValue].value}
        facilityLocationsTerm={facilityLocationsTerm}
      />

      <div className={classes.table}>
        {!initialLoading && !!eventsToShow.length ? (
          <JournalTable
            {...props}
            classes={classes}
            eventsToShow={eventsToShow}
            journal={journal}
            loading={inLoadingState}
            startIndex={startIndex}
            tabData={tabData}
            onLoadMore={onLoadMore}
            setStartIndex={setStartIndex}
          />
        ) : null}
        {!initialLoading && !loading && !eventsToShow.length ? (
          <EmptyJournal
            data-testid="all-events-journal-empty-state"
            classes={classes}
            annotationTypes={types}
            facilityLocationsTerm={facilityLocationsTerm}
            rooms={rooms}
            checkedRooms={checkedRooms}
            searchTerms={searchTerms}
            relatedUser={relatedUser}
            relatedUsers={relatedUsers}
          />
        ) : null}
        {inLoadingState && !initialLoading
          ? <CircularProgress color="primary" className={classes.journalLoading} size={32} />
          : null}
      </div>
    </section>
  )
}

AllEventsJournalComponent.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  deviceCount: PropTypes.number.isRequired,
  facilityLocationsTerm: PropTypes.func.isRequired,
  getCategory: PropTypes.func.isRequired,
  initialTabValue: PropTypes.oneOf(Array.from(initialTabsData, (_, i) => i)).isRequired,
  initialTaskCategory: idPropType.nullable,
  initialShowComplete: PropTypes.bool.isRequired,
  journal: PropTypes.shape({
    before: PropTypes.number,
    items: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
    loading: PropTypes.bool,
    params: PropTypes.shape({
      start: datePropType,
      end: datePropType,
      // types: PropTypes.arrayOf(PropTypes.oneOf(uniq(tabsData.flatMap(prop('annotationTypes'))))),
      category: idPropType.nullable,
      search: PropTypes.string,
      room: PropTypes.arrayOf(idPropType),
      completed: PropTypes.bool,
      relatedUser: PropTypes.number
    })
  }).isRequired,
  me: PropTypes.shape({
    id: idPropType,
  }).isRequired,
  relatedUsers: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)).isRequired,
  rooms: PropTypes.objectOf(idAndNameShape).isRequired,
  tabsData: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    testId: PropTypes.string.isRequired,
    annotationTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
    params: PropTypes.shape({
      category: idPropType.nullable,
      relatedUser: PropTypes.number,
      room: PropTypes.arrayOf(idPropType),
      search: PropTypes.string,
      showComplete: PropTypes.bool,
      taskBehavior: PropTypes.string,
      types: PropTypes.arrayOf(PropTypes.string).isRequired,
    }).isRequired,
  })).isRequired,
  doClearJournal: PropTypes.func.isRequired,
  doJournalFetch: PropTypes.func.isRequired,
  doUpdateQuery: PropTypes.func.isRequired,
}

export const connectedPropNames = [
  'selectCurrentFacility',
  'selectDevices',
  'selectFacilityLocationsTerm',
  'selectJournal',
  'selectMe',
  'selectMemberships',
  'selectQueryObject',
  'selectRooms',
  'selectTaskCategories',
  'selectUsers',
  'selectZones',
  'doJournalFetch',
  'doClearJournal',
  'doUpdateQuery',
]

const Wrapped = React.memo(props => {
  const {
    devices,
    queryObject,
    taskCategories,
    ...connectedProps
  } = useConnect(...connectedPropNames)

  const { tab } = queryObject
  const initialShowComplete = getQueryParam(queryObject, 'showComplete', true)
  const relatedUsers = getUsers(connectedProps)
  const tabsData = useTabsData()

  const getCategory = useCallback(data => getTaskCategory(Object.values(taskCategories), data), [taskCategories])

  const initialTaskCategory = useMemo(() => {
    const categories = Object.values(taskCategories)
    if (tab === 'tasks') {
      return Number(categories.find(category => !category.taskBehavior)?.id)
    }
    if (tab === 'ipm') {
      return Number(categories.find(category => category.taskBehavior === 'PEST')?.id)
    }
    return null
  }, [tab, taskCategories])

  const { doUpdateQuery } = connectedProps
  useEffect(() => {
    doUpdateQuery({ tab: tab ?? tabsData[0].value }, { replace: true })
  }, [doUpdateQuery, tabsData, tab])

  return (
    <AllEventsJournalComponent
      {...props}
      {...connectedProps}
      tabsData={tabsData}
      deviceCount={Object.keys(devices).length}
      getCategory={getCategory}
      initialTabValue={Math.max(0, tabsData.findIndex(({ value }) => value === tab))}
      initialTaskCategory={initialTaskCategory}
      initialShowComplete={initialShowComplete}
      classes={useStyles({ ...props, isTasksTab: tab === TASKS_TAB })}
      relatedUsers={relatedUsers}
    />
  )
})
Wrapped.displayName = `Wrapped${AllEventsJournalComponent.displayName}`

export default Wrapped
